"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { formatClock, parseClock } from "./format";

/** Seconds nudged per arrow-button click (Shift = 10×). */
const NUDGE_STEP = 0.1;

/**
 * Always-visible, editable timecode "tooltip" pinned to a handle: a compact chip
 * with ◀/▶ fine-step buttons around an editable mm:ss.mmm field. Stops pointer
 * events from reaching the scrub track underneath.
 */
export function TimecodeChip({
  ariaLabel,
  value,
  onCommit,
  onStep,
  disabled,
}: {
  ariaLabel: string;
  value: number;
  onCommit: (seconds: number) => void;
  onStep: (delta: number) => void;
  disabled: boolean;
}) {
  const t = useTranslations("mediaTools.timecodeChip");
  const [text, setText] = useState(() => formatClock(value));
  const [editing, setEditing] = useState(false);

  // Reflect external changes (handle drags, keyboard) unless the user is typing.
  useEffect(() => {
    if (!editing) setText(formatClock(value));
  }, [value, editing]);

  const commit = () => {
    setEditing(false);
    const parsed = parseClock(text);
    if (parsed === null) setText(formatClock(value));
    else onCommit(parsed);
  };

  const step = (direction: -1 | 1) => (event: React.MouseEvent) => {
    event.stopPropagation();
    onStep(direction * NUDGE_STEP * (event.shiftKey ? 10 : 1));
  };

  return (
    <div
      className="flex items-center gap-0.5 rounded-md border bg-background px-1 py-0.5 shadow-xs"
      onPointerDown={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        aria-label={t("nudgeEarlier", { label: ariaLabel })}
        disabled={disabled}
        className="grid h-5 w-5 shrink-0 place-items-center rounded text-foreground/70 hover:bg-foreground/10 disabled:opacity-40"
        onClick={step(-1)}
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </button>
      <input
        type="text"
        inputMode="decimal"
        aria-label={ariaLabel}
        className="w-18 bg-transparent text-center font-mono text-xs outline-hidden disabled:opacity-50"
        value={text}
        disabled={disabled}
        onFocus={() => setEditing(true)}
        onChange={(event) => setText(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
        }}
      />
      <button
        type="button"
        aria-label={t("nudgeLater", { label: ariaLabel })}
        disabled={disabled}
        className="grid h-5 w-5 shrink-0 place-items-center rounded text-foreground/70 hover:bg-foreground/10 disabled:opacity-40"
        onClick={step(1)}
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
